<template>
	<div class="FilterList">
		<CheckBoxSelectItem
			v-for="item in displayedItems"
			:key="item.value"
			:item="item"
			@selectionChange="(state) => handleSelectionChange(item, state)"
		/>

		<button
			v-if="showMoreIndicator"
			type="button"
			class="ShowAll"
			:aria-expanded="showAll"
			:aria-label="showAll ? 'Show less' : `Show all (${items.length})`"
			@click="showHideAll"
		>
			{{ showAllText }}
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import CheckBoxSelectItem from '@components/CheckBoxSelectItem/CheckBoxSelectItem.vue';
import { DegreeTypeFilterMapper } from '@app/routing/mappers/DegreeTypeFilterMapper';
import { useCheckboxFilter } from '@composables/useCheckboxFilter';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { sortSelectItems } from '@app/utils/sort-helper';

const ITEMS_DISPLAY_LIMIT = 7;

const { items, handleSelectionChange } = useCheckboxFilter(
	DegreeTypeFilterMapper.getInstance(),
	FilterType.DEGREE_TYPE
);

const showAll = ref(false);

const sortedItems = computed(() => sortSelectItems(items.value));

const showMoreIndicator = computed(() => {
	return sortedItems.value.length > ITEMS_DISPLAY_LIMIT;
});

const displayedItems = computed(() => {
	if (showAll.value) {
		return sortedItems.value;
	}
	return sortedItems.value.slice(0, ITEMS_DISPLAY_LIMIT);
});

const showAllText = computed(() => {
	return showAll.value ? `Show less` : `Show all (${sortedItems.value.length})`;
});

const showHideAll = () => {
	showAll.value = !showAll.value;
};
</script>

<style scoped lang="scss">
.FilterList {
	width: 100%;
	padding-right: 2px;	// required for outline not to be cut off

	.ShowAll {
		cursor: pointer;
		color: $Blue-700;
		background: none;
		border: none;

		@include PaddingAreas(0.5, 0);
		@include TextStyle(BodyMediumRegular);

		&:focus-visible {
			outline: none;
		}
	}
}
</style>
